import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { REQUIRE_AUTH } from '../../shared/interceptors/auth-context';

export type InvitationChannel = 'whatsapp' | 'email';
export type InvitationStatus = 'pending' | 'accepted' | 'revoked' | 'expired';

export interface TeamRole {
  roleId: string;
  code: string;
  name: string;
}

export interface TeamUser {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string | null;
  avatarUrl?: string | null;
  roles: TeamRole[];
  /** Has a password and can sign in */
  hasAccount: boolean;
  isActive: boolean;
  isCurrentUser: boolean;
}

export interface TeamInvitation {
  userInvitationId: string;
  slug: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string | null;
  role: TeamRole;
  professionalUserId?: string | null;
  channel: InvitationChannel;
  status: InvitationStatus;
  expiresAt: string;
  createdAt: string;
}

export interface TeamUsersResponse {
  users: TeamUser[];
  invitations: TeamInvitation[];
  invitableRoles: TeamRole[];
  canInvite: boolean;
}

export interface CreateInvitationRequest {
  roleId: string;
  professionalUserId?: string | null;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string | null;
  countryId?: string | null;
  channel: InvitationChannel;
}

export interface CreateInvitationResult {
  userInvitationId: string;
  slug: string;
  invitationUrl: string;
  channel: InvitationChannel;
  sent: boolean;
  deliveryError?: string | null;
  expiresAt: string;
}

export interface PublicInvitation {
  slug: string;
  status: InvitationStatus;
  businessName: string;
  businessLogoUrl?: string | null;
  businessPrimaryColor?: string | null;
  roleCode: string;
  roleName: string;
  isProfessional: boolean;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string | null;
  countryId?: string | null;
  expiresAt: string;
}

export interface AcceptInvitationRequest {
  firstName: string;
  lastName: string;
  phone: string;
  countryId: string;
  password: string;
}

export interface AcceptInvitationResult {
  email: string;
  businessName: string;
  roleName: string;
}

/** Invitation pages live at the root of the app; business slugs never contain "--" */
export const INVITATION_SLUG_PATTERN = /^[a-z0-9-]+--[a-z0-9]+$/;

@Injectable({ providedIn: 'root' })
export class TeamService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/api/v1`;
  private auth = { context: new HttpContext().set(REQUIRE_AUTH, true) };

  getUsers(): Observable<TeamUsersResponse> {
    return this.http.get<TeamUsersResponse>(`${this.baseUrl}/team/users`, this.auth);
  }

  invite(request: CreateInvitationRequest): Observable<CreateInvitationResult> {
    return this.http.post<CreateInvitationResult>(`${this.baseUrl}/team/invitations`, request, this.auth);
  }

  revoke(userInvitationId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/team/invitations/${userInvitationId}`, this.auth);
  }

  getPublicInvitation(slug: string): Observable<PublicInvitation> {
    return this.http.get<PublicInvitation>(`${this.baseUrl}/public/invitations/${encodeURIComponent(slug)}`);
  }

  acceptInvitation(slug: string, request: AcceptInvitationRequest): Observable<AcceptInvitationResult> {
    return this.http.post<AcceptInvitationResult>(
      `${this.baseUrl}/public/invitations/${encodeURIComponent(slug)}/accept`,
      request
    );
  }

  /** Link to share by hand, built from where the app is running */
  invitationLink(slug: string): string {
    return `${window.location.origin}/${slug}`;
  }
}
